import PropTypes from 'prop-types';

const cn = (...classes) => classes.filter(Boolean).join(' ');

const BASE =
	'inline-flex min-w-0 items-center justify-center gap-2 whitespace-nowrap border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed';

const SIZES = {
	md: 'h-[38px] px-4 text-md',
	sm: 'h-8 px-3 text-sm',
};

const VARIANTS = {
	solid: 'bg-primary-500 border-primary-500 text-white hover:bg-primary-600 hover:border-primary-600',
	outline: 'bg-transparent border-[1.5px] border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-white',
	danger: 'bg-warning-700 border-warning-700 text-white hover:brightness-110',
	accent: 'bg-accent-gradient border-transparent text-text font-bold hover:brightness-95',
	white: 'bg-white border-white text-primary-700 font-semibold hover:bg-bgBase-300 hover:border-bgBase-300',
	dark: 'bg-sidebar-gradient border-transparent text-white hover:brightness-150',
};

const SHAPES = {
	square: 'rounded-control',
	pill: 'rounded-full',
};

export default function Button({
	children,
	variant = 'solid',
	shape = 'square',
	size = 'md',
	fullWidth = false,
	loading = false,
	className,
	disabled,
	href,
	target,
	type = 'button',
	...props
}) {
	const classes = cn(BASE, SIZES[size], SHAPES[shape], VARIANTS[variant], fullWidth && 'w-full', className);

	const spinner = loading && (
		<span className="h-4 w-4 animate-spin rounded-full border-2 border-current/30 border-t-current" />
	);

	if (href) {
		return (
			<a
				href={href}
				target={target}
				className={cn(classes, (loading || disabled) && 'pointer-events-none opacity-50')}
				aria-disabled={loading || disabled}
				{...props}
			>
				{spinner}
				{children}
			</a>
		);
	}

	return (
		<button type={type} className={classes} disabled={loading || disabled} {...props}>
			{spinner}
			{children}
		</button>
	);
}

Button.propTypes = {
	children: PropTypes.node.isRequired,
	onClick: PropTypes.func,
	variant: PropTypes.oneOf(['solid', 'outline', 'danger', 'accent', 'white', 'dark']),
	shape: PropTypes.oneOf(['square', 'pill']),
	size: PropTypes.oneOf(['md', 'sm']),
	disabled: PropTypes.bool,
	loading: PropTypes.bool,
	fullWidth: PropTypes.bool,
	className: PropTypes.string,
	href: PropTypes.string,
	target: PropTypes.string,
	type: PropTypes.oneOf(['button', 'submit', 'reset']),
};
